import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CreatorHeader from '../../components/creator/CreatorHeader';
import PlatformIcon from '../../components/PlatformIcon';

export default function SocialConnected() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const platform = searchParams.get('platform');
  const status = searchParams.get('status');
  const message = searchParams.get('message');

  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/creator/social');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  const getPlatformColor = () => {
    if (platform === 'facebook') return 'from-blue-500 to-blue-600';
    if (platform === 'instagram') return 'from-purple-500 via-pink-500 to-orange-500';
    return 'from-blue-500 to-purple-500';
  };

  const getPlatformName = () => {
    return (platform ? platform.charAt(0).toUpperCase() + platform.slice(1) : 'Social Media');
  };

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gray-50">
        <CreatorHeader />
        
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
          <div className="bg-white rounded-2xl border-2 border-red-200 shadow-xl p-6 sm:p-12 text-center">
            {}
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
              <svg className="w-8 h-8 sm:w-10 sm:h-10 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
              </svg>
            </div>

            {}
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              Connection Failed
            </h1>
            <p className="text-sm sm:text-base text-gray-600 mb-6">
              {message ? decodeURIComponent(message) : 'Something went wrong while connecting to ' + getPlatformName()}
            </p>

            {}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/creator/social')}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all text-sm sm:text-base"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/creator/dashboard')}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm sm:text-base"
              >
                Back to Dashboard
              </button>
            </div>

            {}
            <p className="text-xs sm:text-sm text-gray-500 mt-6">
              Redirecting in {countdown} seconds...
            </p>
          </div>
        </div>
      </div>
    );
  }

  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <CreatorHeader />
      
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-12 text-center relative overflow-hidden">
          {}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-blue-100 rounded-full opacity-50 animate-pulse"></div>
            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-purple-100 rounded-full opacity-50 animate-pulse" style={{ animationDelay: '1s' }}></div>
          </div>

          {}
          <div className="relative z-10">
            {}
            <div className={`w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-r ${getPlatformColor()} rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg animate-bounce`}>
              <PlatformIcon platform={platform} className="w-10 h-10 sm:w-12 sm:h-12 fill-white"/>
            </div>

            {}
            <div className="mb-6">
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm sm:text-base font-semibold mb-4">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
                <span>Connected Successfully!</span>
              </div>

              <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-3">
                {getPlatformName()} Connected! 🎉
              </h1>
              <p className="text-sm sm:text-lg text-gray-600 max-w-md mx-auto">
                Your {getPlatformName()} account is now connected. You can start publishing videos!
              </p>
            </div>

            {}
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-4 sm:p-6 mb-6">
              <h3 className="font-bold text-gray-900 mb-3 text-sm sm:text-base">What you can do now:</h3>
              <div className="space-y-2 text-left max-w-sm mx-auto">
                <div className="flex items-start gap-2 text-xs sm:text-sm text-gray-700">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <span>Publish videos directly to {getPlatformName()}</span>
                </div>
                <div className="flex items-start gap-2 text-xs sm:text-sm text-gray-700">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <span>Choose between Reels, Feed, and Stories</span>
                </div>
                <div className="flex items-start gap-2 text-xs sm:text-sm text-gray-700">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <span>Automatic posting with custom captions</span>
                </div>
                <div className="flex items-start gap-2 text-xs sm:text-sm text-gray-700">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <span>Track all your published content</span>
                </div>
              </div>
            </div>

            {}
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
              <button
                onClick={() => navigate('/creator/videos')}
                className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-bold text-base sm:text-lg hover:shadow-2xl transition-all flex items-center justify-center gap-2"
              >
                <span>Start Publishing</span>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd"/>
                </svg>
              </button>
              <button
                onClick={() => navigate('/creator/social')}
                className="px-6 sm:px-8 py-3 sm:py-4 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all text-sm sm:text-base"
              >
                Manage Connections
              </button>
            </div>

            {}
            <div className="flex items-center justify-center gap-2 text-xs sm:text-sm text-gray-500">
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Redirecting to connections in {countdown} seconds...</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}